<template>
	<CHeaderLayout @show-account-menu="$emit('show-account-menu')">
		<template #title>
			<h1>Users &amp; Authentication</h1>
		</template>

		<main class="organization-settings-users">
			<AuthenticationSettings />
			<COrganizationUsersView
				:users="users"
				:user-usage-message="userUsageMessage"
				:is-paid-plan="isPaidPlan"
				:has-locked-users="hasLockedUsers"
				:is-dunning-restricted="isDunningRestricted"
				:org-code="orgCode"
				:can-assign-permissions-to-folder="canAssignPermissionsToFolder"
				:downgraded-from-enterprise="downgradedFromEnterprise"
				:loading="loading"
				:current-user-global-permission="currentUserGlobalPermission"
				class="mt-5"
				@system-role-changed="getUsersAndUsageInfo"
			/>
		</main>
		<COrganizationAuditLogExportDialog
			v-if="organizationAuditLogDownloadUrl"
			show
			export-status="complete"
			:download-in-progress="false"
			:download-url="organizationAuditLogDownloadUrl"
		/>
	</CHeaderLayout>
</template>

<script lang="ts">
	import { computed, defineComponent, ref, onMounted, onUnmounted } from 'vue';
	import CHeaderLayout from 'src/components/app/HeaderLayout.vue';
	import AuthenticationSettings from '../../organization/AuthenticationSettings.vue';
	import COrganizationUsersView from './OrganizationUsersView.vue';
	import COrganizationAuditLogExportDialog from 'src/components/dialogs/OrganizationAuditLogExportDialog.vue';
	import session from '@cognitoforms/api/services/session';
	import notify from 'src/utilities/notify';
	import { globalStore } from 'src/stores/global-store';
	import { websocketConnection } from 'src/utilities/websocket/websocket-connection-factory';
	import { getUsersInfo } from '@cognitoforms/api/services/organization-users-service';
	import { useOrganizationAuditLogExportDownload } from 'src/composables/use-organization-audit-log-export-download';
	import type { OrganizationUserResponse } from '@cognitoforms/types/server-types/core/services/organization-user-response';

	export default defineComponent({
		components: {
			CHeaderLayout,
			AuthenticationSettings,
			COrganizationUsersView,
			COrganizationAuditLogExportDialog
		},
		emits: ['component-loaded', 'show-account-menu'],
		setup(_, { emit }) {
			const users = ref([] as OrganizationUserResponse[]);
			const userUsageMessage = ref('');
			const isPaidPlan = ref(false);
			const hasLockedUsers = ref(false);
			const isDunningRestricted = ref(false);
			const canAssignPermissionsToFolder = ref(false);
			const downgradedFromEnterprise = ref(false);
			const loading = ref(true);

			const orgCode = computed(() => session.value?.orgCode ?? '');
			const currentUserGlobalPermission = computed(() => globalStore.organization.Role);

			const getUsersAndUsageInfo = async () => {
				const res = await getUsersInfo();
				users.value = res.Users;
				userUsageMessage.value = res.UserUsageMessage;
				isPaidPlan.value = res.IsPaidPlan;
				hasLockedUsers.value = res.HasLockedUsers;
				isDunningRestricted.value = res.IsDunningRestricted;
				canAssignPermissionsToFolder.value = res.CanAssignFolderPermission;
				downgradedFromEnterprise.value = res.DowngradedFromEnterprise;
				loading.value = false;
			};

			let organizationSubscription;
			onMounted(() => {
				getUsersAndUsageInfo();
				organizationSubscription = websocketConnection.subscribeToEvents('Organization', getUsersAndUsageInfo);
				emit('component-loaded');
				const notifyUser = localStorage.getItem('viewAsRedirect');
				if (notifyUser === 'true') {
					notify('You cannot view the organization as this user');
					localStorage.removeItem('viewAsRedirect');
				}
			});

			onUnmounted(() => {
				organizationSubscription.unsubscribe();
			});

			// Organization audit log export links sent via email will open on this page
			const { downloadUrl: organizationAuditLogDownloadUrl } = useOrganizationAuditLogExportDownload();

			return {
				users,
				userUsageMessage,
				isPaidPlan,
				hasLockedUsers,
				isDunningRestricted,
				canAssignPermissionsToFolder,
				downgradedFromEnterprise,
				loading,
				orgCode,
				currentUserGlobalPermission,
				getUsersAndUsageInfo,
				organizationAuditLogDownloadUrl
			};
		},
		head() {
			return {
				title: 'Users & Authentication - Cognito Forms',
				titleTemplate: '%s'
			};
		}
	});
</script>

<style lang="scss">
.organization-settings-users {
	--content-max-width: 896px;
}
</style>